import { useEffect, useRef, useState } from "react";
import { useCurrentUser, useDeleteAccount, useRegenerateRecoveryKey } from "../../../lib/api/users.api";
import { clearUserCache } from "../../../lib/api/client";
import { clearAllData } from "../../../lib/api/sync";
import { RecoveryKeyModal } from "../../auth/components/RecoveryKeyModal";
import { AccountSettings } from "./AccountSettings";
import { ConnectedAccounts } from "./ConnectedAccounts";
import { Overlay } from "../../../ui/atoms/Overlay";
import { SectionLabel, NavRow } from "./SettingsUIComponents";
import { getUserRole } from "../../../lib/api/client";
import { exportJson, exportICalendar } from "./export-import/ExportImportHelpers";
import { validateImportedData } from "./export-import/ExportImportHelpers";
import { ImportDiffModal } from "./export-import/ImportDiffModal";

interface AccountSectionProps {
  readonly onAdmin?: () => void;
  readonly onImport?: (data: { tasks: unknown[]; events: unknown[]; habits?: unknown[] }) => void;
  readonly autoOpenRegenerate?: boolean;
}

const DELETE_PHRASE = "SUPPRIMER MON COMPTE";
const CLEAR_PHRASE = "VIDER MES DONNÉES";

export function AccountSection({ onAdmin, onImport, autoOpenRegenerate }: AccountSectionProps) {
  const { data: user } = useCurrentUser();
  const isAdmin = getUserRole() === "ADMIN";
  const [emailVisible, setEmailVisible] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);

  // Export/Import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importMsg, setImportMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Recovery key state
  const regenerateKey = useRegenerateRecoveryKey();
  const [autoRegen] = useState(autoOpenRegenerate ?? false);
  const [showPasswordForKey, setShowPasswordForKey] = useState(autoRegen);
  const [highlightSecurity, setHighlightSecurity] = useState(autoRegen);
  const securityRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoRegen || !user) return;
    requestAnimationFrame(() => {
      securityRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const timer = setTimeout(() => setHighlightSecurity(false), 3000);
    return () => clearTimeout(timer);
  }, [autoRegen, user]);
  const [keyPassword, setKeyPassword] = useState("");
  const [newRecoveryKey, setNewRecoveryKey] = useState<string | null>(null);

  // Danger zone state
  const deleteAccount = useDeleteAccount();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [typedPhrase, setTypedPhrase] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearPhrase, setClearPhrase] = useState("");

  const phraseMatch = typedPhrase === DELETE_PHRASE;

  const handleLogout = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
    clearUserCache();
    // Assigner href provoque un full reload — pas de re-render React intermédiaire
    window.location.assign("/");
  };

  const handleDeleteClose = () => {
    setConfirmDelete(false);
    setTypedPhrase("");
  };

  const handleDelete = async () => {
    if (!phraseMatch) return;
    try {
      await deleteAccount.mutateAsync();
      clearUserCache();
      window.location.href = "/";
    } catch {
      handleDeleteClose();
    }
  };

  const handleClearData = async () => {
    if (clearPhrase !== CLEAR_PHRASE) return;
    setClearing(true);
    await clearAllData();
    setClearing(false);
    setConfirmClear(false);
    setClearPhrase("");
  };

  const handleExportJson = () => {
    try {
      const tasks = JSON.parse(localStorage.getItem("palnia_tasks") ?? "[]");
      const events = JSON.parse(localStorage.getItem("palnia_events") ?? "[]");
      const habits = JSON.parse(localStorage.getItem("palnia_habits") ?? "[]");
      exportJson(tasks, events, habits);
    } catch { /* ignore */ }
  };

  const handleExportICal = () => {
    try {
      const events = JSON.parse(localStorage.getItem("palnia_events") ?? "[]");
      exportICalendar(events);
    } catch { /* ignore */ }
  };

  const [pendingImport, setPendingImport] = useState<{
    tasks: unknown[];
    events: unknown[];
    habits: unknown[];
  } | null>(null);

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onImport) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string) as unknown;
        const data = validateImportedData(parsed);
        setPendingImport({
          tasks: data.tasks,
          events: data.events,
          habits: data.habits ?? [],
        });
      } catch (err) {
        setImportMsg({ type: "error", text: err instanceof Error ? err.message : "Format invalide" });
        setTimeout(() => setImportMsg(null), 3000);
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleConfirmImport = (data: { tasks: unknown[]; events: unknown[]; habits: unknown[] }) => {
    onImport?.(data);
    setPendingImport(null);
    setImportMsg({ type: "success", text: "Données importées" });
    setTimeout(() => setImportMsg(null), 3000);
  };

  if (!user) return null;

  return (
    <>
      <div className="flex flex-col gap-1 border-t border-border pt-4">
        <SectionLabel>Compte</SectionLabel>
        <div className="flex items-center justify-between py-2">
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-xs font-medium text-ink truncate">{user.name}</span>
            <button
              type="button"
              onClick={() => setEmailVisible((v) => !v)}
              className="text-[11px] text-ink-muted text-left hover:text-ink truncate"
            >
              {emailVisible ? user.email : "••••••••••"}
            </button>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setShowAccountSettings(true)}
              className="px-3 py-1.5 text-[11px] font-medium rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors"
            >
              Modifier
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="px-3 py-1.5 text-[11px] font-medium rounded-lg text-red-500 hover:bg-red-500/10 transition-colors"
            >
              Déconnexion
            </button>
          </div>
        </div>
        {user.role === "ADMIN" && onAdmin && (
          <NavRow label="Administration" onClick={onAdmin} />
        )}

        {/* Export / Import */}
        <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-border/50">
          <SectionLabel>Données</SectionLabel>
          <div className="flex gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={isAdmin ? handleExportJson : undefined}
              disabled={!isAdmin}
              className="px-3 py-1.5 text-[11px] font-medium rounded-lg bg-accent text-white hover:opacity-90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={!isAdmin ? "Disponible dans la version payante" : undefined}
            >
              Exporter JSON
            </button>
            <button
              type="button"
              onClick={isAdmin ? handleExportICal : undefined}
              disabled={!isAdmin}
              className="px-3 py-1.5 text-[11px] font-medium rounded-lg bg-accent text-white hover:opacity-90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={!isAdmin ? "Disponible dans la version payante" : undefined}
            >
              Exporter iCal
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 text-[11px] font-medium rounded-lg text-ink-muted hover:text-ink border border-border/60 hover:bg-surface-alt transition-colors"
            >
              Importer
            </button>
          </div>
          {!isAdmin && (
            <p className="text-[10px] text-ink-faint">
              L'export de données sera disponible à la sortie de la bêta.
            </p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImportFile}
            className="hidden"
            aria-label="Importer fichier JSON"
          />
          {importMsg && (
            <span className={`text-[11px] font-medium ${importMsg.type === "success" ? "text-emerald-500" : "text-danger"}`}>
              {importMsg.text}
            </span>
          )}
        </div>

        {pendingImport && (
          <ImportDiffModal
            existingTasks={JSON.parse(localStorage.getItem("palnia_tasks") ?? "[]")}
            existingEvents={JSON.parse(localStorage.getItem("palnia_events") ?? "[]")}
            existingHabits={JSON.parse(localStorage.getItem("palnia_habits") ?? "[]")}
            importedTasks={pendingImport.tasks as { id?: string; title?: string }[]}
            importedEvents={pendingImport.events as { id?: string; title?: string }[]}
            importedHabits={pendingImport.habits as { id?: string; title?: string }[]}
            onConfirm={handleConfirmImport}
            onCancel={() => setPendingImport(null)}
          />
        )}

        <ConnectedAccounts />

        {/* Sécurité */}
        <div
          ref={securityRef}
          className={`flex flex-col gap-2 mt-3 pt-3 border-t border-border rounded-lg transition-all duration-1000 ${highlightSecurity ? "ring-2 ring-accent ring-offset-2 ring-offset-surface p-3 -mx-1" : ""}`}
        >
          <SectionLabel>Sécurité</SectionLabel>

          <div className="flex items-start gap-3 p-3 bg-green-500/5 border border-green-500/15 rounded-lg">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-green-500 shrink-0 mt-0.5" stroke="currentColor" strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-green-400">Vos données sont protégées</span>
              <p className="text-[10px] text-ink-faint leading-relaxed">
                Tout ce que vous écrivez (titres, notes, tags) est verrouillé avec votre mot de passe avant d'être envoyé au serveur. Personne ne peut lire vos données — même l'équipe Palnia.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-ink">Clé de récupération</span>
              <span className="text-[11px] text-ink-muted">Générer une nouvelle clé (l'ancienne sera invalidée)</span>
            </div>
            <button
              type="button"
              onClick={() => setShowPasswordForKey(true)}
              className="shrink-0 px-3 py-1.5 text-[11px] font-medium rounded-lg text-blue-400 border border-blue-400/30 hover:bg-blue-400/5 transition-colors"
            >
              Régénérer
            </button>
          </div>

          {showPasswordForKey && (
            <div className="flex flex-col gap-2 p-3 bg-surface-alt rounded-lg">
              <p className="text-[11px] text-ink-muted">Entrez votre mot de passe actuel pour générer une nouvelle clé :</p>
              <input
                type="password"
                value={keyPassword}
                onChange={(e) => setKeyPassword(e.target.value)}
                placeholder="Mot de passe actuel"
                autoComplete="current-password"
                className="px-3 py-2 text-sm rounded-lg border border-border bg-surface focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => { setShowPasswordForKey(false); setKeyPassword(""); }}
                  className="px-3 py-1.5 text-[11px] text-ink-muted hover:text-ink rounded-lg"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={!keyPassword || regenerateKey.isPending}
                  onClick={() => {
                    regenerateKey.mutate(
                      { currentPassword: keyPassword },
                      {
                        onSuccess: (data) => {
                          setNewRecoveryKey(data.recoveryKey);
                          setShowPasswordForKey(false);
                          setKeyPassword("");
                        },
                      },
                    );
                  }}
                  className="px-3 py-1.5 text-[11px] font-medium text-white bg-blue-500 rounded-lg hover:opacity-80 disabled:opacity-40 transition-opacity"
                >
                  {regenerateKey.isPending ? "Génération..." : "Générer"}
                </button>
              </div>
              {regenerateKey.isError && (
                <p className="text-[11px] text-red-500">Mot de passe incorrect.</p>
              )}
            </div>
          )}
        </div>

        {/* Zone dangereuse */}
        <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-danger/20">
          <SectionLabel>Zone dangereuse</SectionLabel>
          <div className="flex items-center justify-between py-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-ink">Vider les données</span>
              <span className="text-[11px] text-ink-muted">Supprimer tâches, événements et habitudes</span>
            </div>
            <button
              type="button"
              onClick={() => setConfirmClear(true)}
              className="shrink-0 px-3 py-1.5 text-[11px] font-medium rounded-lg text-amber-600 border border-amber-500/30 hover:bg-amber-500/5 transition-colors"
            >
              Vider
            </button>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-ink">Supprimer le compte</span>
              <span className="text-[11px] text-ink-muted">Toutes vos données seront perdues</span>
            </div>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteAccount.isPending}
              className="shrink-0 px-3 py-1.5 text-[11px] font-medium rounded-lg text-danger border border-danger/30 hover:bg-danger/5 disabled:opacity-50 transition-colors"
            >
              {deleteAccount.isPending ? "Suppression..." : "Supprimer"}
            </button>
          </div>
        </div>
      </div>

      {showAccountSettings && (
        <AccountSettings onClose={() => setShowAccountSettings(false)} />
      )}

      {newRecoveryKey && (
        <RecoveryKeyModal
          recoveryKey={newRecoveryKey}
          onAcknowledge={() => setNewRecoveryKey(null)}
        />
      )}

      {/* Modal confirmation suppression compte */}
      <Overlay open={confirmDelete} onClose={handleDeleteClose}>
        <div className="bg-surface border border-border p-5 w-96 max-w-[90vw] shadow-xl rounded-2xl flex flex-col gap-3">
          <h2 className="text-sm font-bold text-danger">Supprimer mon compte</h2>
          <p className="text-[11px] text-ink-muted leading-relaxed">
            Cette action est irréversible. Toutes vos données seront supprimées définitivement.
          </p>
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] text-ink">
              Tapez <span className="font-bold text-danger">{DELETE_PHRASE}</span> pour confirmer :
            </p>
            <input
              type="text"
              value={typedPhrase}
              onChange={(e) => setTypedPhrase(e.target.value)}
              placeholder={DELETE_PHRASE}
              autoComplete="off"
              spellCheck={false}
              className="w-full px-3 py-2 text-xs text-ink bg-surface border border-border/60 rounded-md focus:outline-none focus:border-danger/60 placeholder:text-ink-faint"
            />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={handleDeleteClose}
              className="px-3 py-2 text-xs font-medium text-ink-muted hover:text-ink rounded-md"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!phraseMatch || deleteAccount.isPending}
              className="px-3 py-2 text-xs font-medium bg-danger text-white rounded-md hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {deleteAccount.isPending ? "Suppression..." : "Supprimer"}
            </button>
          </div>
        </div>
      </Overlay>

      {/* Modal confirmation vider données */}
      <Overlay open={confirmClear} onClose={() => { setConfirmClear(false); setClearPhrase(""); }}>
        <div className="bg-surface border border-border p-5 w-96 max-w-[90vw] shadow-xl rounded-2xl flex flex-col gap-3">
          <h2 className="text-sm font-bold text-amber-600">Vider les données</h2>
          <p className="text-[11px] text-ink-muted leading-relaxed">
            Toutes vos tâches, événements et habitudes seront supprimés définitivement. Cette action est irréversible.
          </p>
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] text-ink">
              Tapez <span className="font-bold text-amber-600">{CLEAR_PHRASE}</span> pour confirmer :
            </p>
            <input
              type="text"
              value={clearPhrase}
              onChange={(e) => setClearPhrase(e.target.value)}
              placeholder={CLEAR_PHRASE}
              autoComplete="off"
              spellCheck={false}
              className="w-full px-3 py-2 text-xs text-ink bg-surface border border-border/60 rounded-md focus:outline-none focus:border-amber-500/60 placeholder:text-ink-faint"
            />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={() => { setConfirmClear(false); setClearPhrase(""); }}
              className="px-3 py-2 text-xs font-medium text-ink-muted hover:text-ink rounded-md"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleClearData}
              disabled={clearPhrase !== CLEAR_PHRASE || clearing}
              className="px-3 py-2 text-xs font-medium bg-amber-500 text-white rounded-md hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {clearing ? "Suppression..." : "Vider les données"}
            </button>
          </div>
        </div>
      </Overlay>
    </>
  );
}
